/**
 * BR Code do PIX ESTÁTICO — o QR que vai em todo orçamento (EMV/BCB).
 *
 * Com o BaaS do Asaas congelado (addendum do #165), este é o ÚNICO caminho de
 * pagamento dos PDFs do app. Testes em `src/test/pixQrCode.test.ts`.
 *
 * ─── A REGRA QUE FAZ ESTE ARQUIVO LANÇAR EM VEZ DE DEGRADAR ─────────────────
 *
 * Chave que não se encaixa em nenhum dos cinco formatos do Pix (e-mail, chave
 * aleatória, telefone, CPF, CNPJ) **não vira BR Code**: `classifyPixKey`
 * devolve `null` e `generatePixPayload` LANÇA.
 *
 * Antes, o fallback devolvia "só os dígitos" — e com texto livre isso é string
 * vazia. O payload saía com `0100` (chave de comprimento zero), o CRC fechava,
 * o QR renderizava no PDF e **nenhum banco conseguia pagar**. Não havia erro,
 * log nem toast: o prestador só descobria pelo cliente reclamando, dias depois.
 * Gerar nada é melhor do que gerar um código morto, e é por isso que
 * `pixQrEligible` (em `pdfTemplates/shared.ts`) consulta `isPixKeyEncodable`
 * antes de desenhar a caixa — senão o papel sairia com "Escaneie o QR Code ao
 * lado" e um espaço em branco.
 *
 * `classifyPixKey` é também a FONTE ÚNICA do rótulo que o formulário da empresa
 * mostra. Antes havia uma segunda implementação, duplicada em `CompanyFormPage`
 * e `CompanyFormModal`, que classificava texto livre como "Outro" e exibia um
 * ✓ verde — o app CONFIRMANDO uma chave que ele não conseguia codificar.
 */

interface PixPayload {
  pixKey: string;
  merchantName: string;
  merchantCity?: string;
  amount?: number;
  txId?: string;
  description?: string;
}

/**
 * Formats a field in TLV format: ID + Length(2 digits) + Value
 */
function formatField(id: string, value: string): string {
  const length = value.length.toString().padStart(2, '0');
  return `${id}${length}${value}`;
}

/**
 * Validates CPF using check digit algorithm
 * This is essential to distinguish CPF (11 digits) from phone numbers (11 digits)
 */
function isValidCPF(cpf: string): boolean {
  const strCPF = cpf.replace(/[^\d]/g, '');
  
  // Must be exactly 11 digits and not all same digit
  if (strCPF.length !== 11 || /^(\d)\1+$/.test(strCPF)) return false;
  
  // First check digit validation
  let sum = 0;
  for (let i = 1; i <= 9; i++) {
    sum += parseInt(strCPF.substring(i - 1, i)) * (11 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(strCPF.substring(9, 10))) return false;
  
  // Second check digit validation
  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(strCPF.substring(i - 1, i)) * (12 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(strCPF.substring(10, 11))) return false;
  
  return true;
}

/**
 * Normalizes text: removes accents, converts to uppercase, keeps only alphanumeric and spaces
 */
function normalizeText(text: string, limit: number): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-zA-Z0-9 ]/g, '')   // Keep only alphanumeric and spaces
    .toUpperCase()
    .trim()
    .substring(0, limit) || 'RECEBEDOR';
}

/** Os cinco formatos de chave que o Pix reconhece. */
export type PixKeyType = 'email' | 'random' | 'phone' | 'cpf' | 'cnpj';

export interface PixKeyClassification {
  /** `null` = não é nenhum formato de chave Pix válido. */
  type: PixKeyType | null;
  /** A chave normalizada, exatamente como vai ao BR Code. `null` se inválida. */
  value: string | null;
  /** Rótulo pt-BR para a tela. `null` se inválida. */
  label: string | null;
}

const INVALID: PixKeyClassification = { type: null, value: null, label: null };

/**
 * Classifica e NORMALIZA uma chave Pix. Fonte única: o BR Code e o formulário
 * da empresa consultam esta função, para a tela não poder aprovar uma chave que
 * o gerador recusa (era o caso do "✓ Tipo detectado: Outro").
 *
 * ⚠️ NÃO loga a chave. O PDF é gerado NO CLIENTE, inclusive na página pública
 * `/o/:id` — um `console.log` aqui despeja o CPF/e-mail/telefone do prestador
 * no navegador de todo cliente que abre o orçamento.
 */
export function classifyPixKey(key: string | null | undefined): PixKeyClassification {
  const trimmed = (key ?? '').trim();
  if (!trimmed) return INVALID;

  // E-mail: qualquer coisa com @. Permissivo de propósito — validar e-mail por
  // regex rejeita endereços legítimos, e quem valida de verdade é o banco.
  if (trimmed.includes('@')) {
    return { type: 'email', value: trimmed.toLowerCase(), label: 'E-mail' };
  }

  // Chave aleatória (EVP): UUID de 36 caracteres.
  if (trimmed.length > 20 && trimmed.includes('-')) {
    return { type: 'random', value: trimmed.toLowerCase(), label: 'Chave aleatória' };
  }

  // Já internacional. Chave de telefone do Pix é sempre brasileira: `+55` mais
  // 10 (fixo) ou 11 (celular) dígitos. Aceitar `+1…` seria gravar uma chave que
  // não existe no arranjo.
  if (trimmed.startsWith('+')) {
    const limpo = trimmed.replace(/[^\d+]/g, '');
    if (!/^\+55\d{10,11}$/.test(limpo)) return INVALID;
    return { type: 'phone', value: limpo, label: limpo.length === 14 ? 'Celular' : 'Telefone fixo' };
  }

  const onlyDigits = trimmed.replace(/\D/g, '');

  // 11 dígitos são CPF **ou** celular — os dois têm o mesmo tamanho. O dígito
  // verificador é o que separa: tratar um CPF como telefone produziria
  // `+5511144477735`, uma chave que não existe, e o dinheiro nunca chegaria.
  if (onlyDigits.length === 11) {
    return isValidCPF(onlyDigits)
      ? { type: 'cpf', value: onlyDigits, label: 'CPF' }
      : { type: 'phone', value: `+55${onlyDigits}`, label: 'Celular' };
  }

  if (onlyDigits.length === 10) {
    return { type: 'phone', value: `+55${onlyDigits}`, label: 'Telefone fixo' };
  }

  if (onlyDigits.length === 14) {
    return { type: 'cnpj', value: onlyDigits, label: 'CNPJ' };
  }

  // 12 ou 13 dígitos começando em 55: é o telefone com código de país, sem o
  // `+` — o formato que sai de "copiar o número" em muitos aplicativos. Antes
  // caía no fallback e ia ao papel cru, sem o `+` que o padrão exige.
  if ((onlyDigits.length === 12 || onlyDigits.length === 13) && onlyDigits.startsWith('55')) {
    return {
      type: 'phone',
      value: `+${onlyDigits}`,
      label: onlyDigits.length === 13 ? 'Celular' : 'Telefone fixo',
    };
  }

  // Nenhum formato conhecido. NÃO existe fallback: ver o cabeçalho do arquivo.
  return INVALID;
}

/**
 * A chave dá para virar BR Code? Consultado por `pixQrEligible` ANTES de
 * desenhar a caixa de Pix no PDF — sem isso, o documento sairia com a instrução
 * "Escaneie o QR Code ao lado" e nenhum QR ao lado.
 */
export function isPixKeyEncodable(key: string | null | undefined): boolean {
  return classifyPixKey(key).value !== null;
}

/**
 * Computes CRC16 (Polynomial 0x1021, CCITT-FALSE)
 */
function computeCRC(str: string): string {
  let crc = 0xFFFF;
  
  for (let i = 0; i < str.length; i++) {
    crc ^= str.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      if ((crc & 0x8000) !== 0) {
        crc = (crc << 1) ^ 0x1021;
      } else {
        crc = crc << 1;
      }
    }
  }
  
  return (crc & 0xFFFF).toString(16).toUpperCase().padStart(4, '0');
}

export function generatePixPayload(data: PixPayload): string {
  const { pixKey, merchantName, merchantCity, amount, txId, description } = data;

  // LANÇA em chave irreconhecível, em vez de devolver um código impagável —
  // ver o cabeçalho do arquivo. Os chamadores (`buildPixQrDataUrl`,
  // `generatePixQrCodeDataUrl`) já degradam para "sem QR".
  const finalKey = classifyPixKey(pixKey).value;
  if (!finalKey) {
    throw new Error('Chave Pix inválida: não é e-mail, telefone, CPF, CNPJ nem chave aleatória.');
  }

  const safeName = normalizeText(merchantName || 'RECEBEDOR', 25);

  // Use provided city or default to SAO PAULO (mandatory in EMV standard)
  const safeCity = normalizeText(merchantCity || 'SAO PAULO', 15);

  // Build Merchant Account Info (ID 26) - GUI must be lowercase per BCB spec
  let merchantAccountInfo = formatField('00', 'br.gov.bcb.pix') + formatField('01', finalKey);
  if (description) {
    const safeDesc = normalizeText(description, 72);
    merchantAccountInfo += formatField('02', safeDesc);
  }

  // Build payload following EMV QRCPS structure
  let payload = '000201'; // Payload Format Indicator
  payload += formatField('26', merchantAccountInfo); // Merchant Account Info
  payload += formatField('52', '0000'); // Merchant Category Code
  payload += formatField('53', '986'); // Transaction Currency (BRL)
  
  // Transaction Amount (optional)
  if (amount && amount > 0) {
    payload += formatField('54', amount.toFixed(2));
  }
  
  payload += formatField('58', 'BR'); // Country Code
  payload += formatField('59', safeName); // Merchant Name
  payload += formatField('60', safeCity); // Merchant City
  
  // Additional Data Field (ID 62) with Reference Label (ID 05)
  const referenceLabel = txId ? txId.slice(0, 25) : '***';
  payload += formatField('62', formatField('05', referenceLabel));
  
  // Add CRC placeholder and calculate
  payload += '6304';
  const crc = computeCRC(payload);
  
  return payload + crc;
}

/**
 * Generates a QR code as a data URL from a PIX payload
 */
export async function generatePixQrCodeDataUrl(data: PixPayload): Promise<string | null> {
  try {
    const payload = generatePixPayload(data);
    
    // Dynamic import for qrcode library
    const QRCode = await import('qrcode');
    
    const dataUrl = await QRCode.toDataURL(payload, {
      errorCorrectionLevel: 'M',
      margin: 2,
      width: 200,
      color: {
        dark: '#000000',
        light: '#FFFFFF',
      },
    });
    
    return dataUrl;
  } catch (error) {
    console.error('Error generating PIX QR code:', error);
    return null;
  }
}
